import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { softDelete } from '../../lib/softDelete';
import { supabase } from '../../lib/supabase';
import type {
  InvoiceStatus,
  PaymentMethod,
  ProjectInvoice,
  ProjectPayment,
} from './projectFinance';

const fromPayments = () => supabase.from('project_payments');

export const projectFinanceKey = (orgId: string | null, projectId: string | null) =>
  ['project-finance', orgId, projectId] as const;

const INVOICE_COLUMNS =
  'id, project_id, invoice_number, status, amount, currency, sent_at, paid_at, notes, created_at';
const PAYMENT_COLUMNS =
  'id, project_id, invoice_id, amount, currency, paid_at, method, notes, created_at';

export interface ProjectFinanceData {
  invoices: ProjectInvoice[];
  payments: ProjectPayment[];
}

async function fetchFinance(orgId: string, projectIds: string[]): Promise<ProjectFinanceData> {
  if (projectIds.length === 0) return { invoices: [], payments: [] };
  const [inv, pay] = await Promise.all([
    supabase
      .from('project_invoices')
      .select(INVOICE_COLUMNS)
      .eq('organization_id', orgId)
      .in('project_id', projectIds)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
    fromPayments()
      .select(PAYMENT_COLUMNS)
      .eq('organization_id', orgId)
      .in('project_id', projectIds)
      .is('deleted_at', null)
      .order('paid_at', { ascending: false }),
  ]);
  if (inv.error) throw inv.error;
  if (pay.error) throw pay.error;
  // NUMERIC(12,2) arrives from PostgREST as a JSON number.
  return {
    invoices: inv.data as unknown as ProjectInvoice[],
    payments: pay.data as unknown as ProjectPayment[],
  };
}

export function useProjectFinance(orgId: string | null, projectId: string | null) {
  return useQuery({
    queryKey: projectFinanceKey(orgId, projectId),
    queryFn: () =>
      orgId && projectId
        ? fetchFinance(orgId, [projectId])
        : Promise.resolve<ProjectFinanceData>({ invoices: [], payments: [] }),
    enabled: Boolean(orgId && projectId),
    staleTime: 15 * 1000,
  });
}

/** Invoices and payments for many projects at once — a customer's whole book. */
export function useProjectsFinance(orgId: string | null, projectIds: string[]) {
  const sorted = [...projectIds].sort();
  return useQuery({
    queryKey: ['project-finance', orgId, 'many', sorted.join(',')] as const,
    queryFn: () =>
      orgId
        ? fetchFinance(orgId, sorted)
        : Promise.resolve<ProjectFinanceData>({ invoices: [], payments: [] }),
    enabled: Boolean(orgId),
    staleTime: 15 * 1000,
  });
}

function useInvalidateFinance(orgId: string | null) {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ['project-finance', orgId] });
    // Customer page KPIs and the finance page read invoices too.
    await qc.invalidateQueries({ queryKey: ['customer-profile'] });
  };
}

export interface InvoiceInput {
  invoice_number: string;
  amount: number;
  sent_at: string;
  /** 'paid' is set by the database once payments cover the amount (0025). */
  status: InvoiceStatus;
  notes: string;
}

function blankToNull(v: string): string | null {
  const t = v.trim();
  return t.length > 0 ? t : null;
}

export function useUpsertInvoice(orgId: string | null, userId: string | null, projectId: string) {
  const invalidate = useInvalidateFinance(orgId);
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: InvoiceInput }) => {
      if (!orgId || !userId) throw new Error('Missing context');
      const fields = {
        invoice_number: values.invoice_number.trim(),
        amount: values.amount,
        sent_at: blankToNull(values.sent_at),
        status: values.status,
        notes: blankToNull(values.notes),
      };
      const { error } = id
        ? await supabase
            .from('project_invoices')
            .update(fields)
            .eq('id', id)
            .eq('organization_id', orgId)
        : await supabase.from('project_invoices').insert({
            ...fields,
            organization_id: orgId,
            project_id: projectId,
            created_by: userId,
          });
      if (error) {
        if (error.code === '23505') throw new Error('כבר יש חשבונית עם המספר הזה');
        throw error;
      }
    },
    onSuccess: invalidate,
  });
}

export function useDeleteInvoice(orgId: string | null) {
  const invalidate = useInvalidateFinance(orgId);
  return useMutation({
    mutationFn: (id: string) => softDelete('project_invoices', id),
    onSuccess: invalidate,
  });
}

export interface PaymentInput {
  amount: number;
  paid_at: string;
  method: PaymentMethod;
  invoice_id: string;
  notes: string;
}

export function useUpsertPayment(orgId: string | null, userId: string | null, projectId: string) {
  const invalidate = useInvalidateFinance(orgId);
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: PaymentInput }) => {
      if (!orgId || !userId) throw new Error('Missing context');
      const fields = {
        amount: values.amount,
        paid_at: values.paid_at,
        method: values.method,
        invoice_id: values.invoice_id || null,
        notes: blankToNull(values.notes),
      };
      const { error } = id
        ? await fromPayments()
            .update(fields)
            .eq('id', id)
            .eq('organization_id', orgId)
        : await fromPayments().insert({
            ...fields,
            organization_id: orgId,
            project_id: projectId,
            created_by: userId,
          });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useDeletePayment(orgId: string | null) {
  const invalidate = useInvalidateFinance(orgId);
  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('Missing context');
      // project_payments' SELECT policy does not filter deleted_at, so a plain
      // update works here (unlike the tables in softDelete.ts).
      const { error } = await fromPayments()
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .eq('organization_id', orgId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
